import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { MultiImageUploader } from "@/components/admin/MultiImageUploader";
import { SmartImage } from "@/components/SmartImage";
import { fetchProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, formatPHP } from "@/lib/api";
import { PRODUCT_CATEGORIES, CATEGORY_LABEL } from "@/data/site";

const emptyProduct = { name: "", category: "apparel", line: "", tagline: "", description: "", benefits: [], features: [], science: [], variants: [], ingredients: [], pricePHP: 0, imageUrl: "", images: [], isFeatured: false };

export const ProductManager = ({ onChanged }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product id or null (new)
  const [form, setForm] = useState(emptyProduct);
  const [benefitsText, setBenefitsText] = useState("");
  const [featuresText, setFeaturesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetchProducts().then(setProducts).catch(() => setProducts([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyProduct);
    setBenefitsText("");
    setFeaturesText("");
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...emptyProduct,
      ...p,
      images: (p.images && p.images.length) ? p.images : (p.imageUrl ? [p.imageUrl] : []),
    });
    setBenefitsText((p.benefits || []).join("\n"));
    setFeaturesText((p.features || []).join("\n"));
    setOpen(true);
  };

  const set = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }));

  const addScience = () => setForm((s) => ({ ...s, science: [...(s.science || []), { title: "", desc: "" }] }));
  const updateScience = (i, key, val) => setForm((s) => {
    const arr = [...(s.science || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...s, science: arr };
  });
  const removeScience = (i) => setForm((s) => ({ ...s, science: (s.science || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name || form.name.trim().length < 1) {
      toast.error("Kailangan ng pangalan ng produkto");
      return;
    }
    const imgs = (form.images || []).map((x) => (x || "").trim()).filter(Boolean);
    const payload = {
      ...form,
      images: imgs,
      imageUrl: imgs[0] || form.imageUrl || "",
      pricePHP: Number(form.pricePHP) || 0,
      benefits: benefitsText.split("\n").map((x) => x.trim()).filter(Boolean),
      features: featuresText.split("\n").map((x) => x.trim()).filter(Boolean),
      science: (form.science || []).map((s) => ({ title: (s.title || "").trim(), desc: (s.desc || "").trim() })).filter((s) => s.title || s.desc),
    };
    setSaving(true);
    try {
      if (editing) await adminUpdateProduct(editing, payload);
      else await adminCreateProduct(payload);
      toast.success(editing ? "Na-update ang produkto" : "Naidagdag ang produkto");
      setOpen(false);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error("Hindi na-save ang produkto");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await adminDeleteProduct(p.id);
      toast.success("Na-delete ang produkto");
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error("Hindi na-delete");
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Mga produkto ({products.length})</h2>
          <p className="text-sm text-muted-foreground">Mag-add, mag-edit, o mag-delete ng produkto — kasama ang larawan at presyo.</p>
        </div>
        <Button onClick={openNew} data-testid="product-add-button"><Plus className="mr-2 h-4 w-4" /> Bagong produkto</Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <div className="p-6 text-sm text-muted-foreground">Naglo-load...</div>}
        {!loading && products.map((p) => (
          <Card key={p.id} className="overflow-hidden rounded-xl border-border bg-card" data-testid="admin-product-card">
            <div className="flex gap-3 p-3">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                <SmartImage src={p.imageUrl} alt={p.name} fallbackLabel={p.line || p.name} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate text-sm font-semibold">{p.name}</p>
                  {p.isFeatured && <Star className="h-4 w-4 shrink-0 fill-current text-accent" />}
                </div>
                <p className="mt-0.5 text-xs font-medium text-foreground">{CATEGORY_LABEL[p.category] || p.category} • {formatPHP(p.pricePHP)}</p>
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => openEdit(p)} data-testid="admin-product-edit"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-destructive" data-testid="admin-product-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="dark">
                      <AlertDialogHeader>
                        <AlertDialogTitle>I-delete ang produkto?</AlertDialogTitle>
                        <AlertDialogDescription>Sigurado ka bang idi-delete ang “{p.name}”? Hindi na ito maibabalik.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Kanselahin</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(p)}>I-delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="dark max-h-[90vh] max-w-lg overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "I-edit ang produkto" : "Bagong produkto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-foreground">Pangalan</Label>
              <Input className="mt-1 h-10" value={form.name} onChange={set("name")} placeholder="Product name" data-testid="product-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-foreground">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="product-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="dark">
                    {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-foreground">Product line</Label>
                <Input className="mt-1 h-10" value={form.line} onChange={set("line")} placeholder="e.g. elevante" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Tagline (maikling deskripsyon)</Label>
              <Input className="mt-1 h-10" value={form.tagline} onChange={set("tagline")} />
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Buong deskripsyon</Label>
              <Textarea className="mt-1 min-h-24" value={form.description} onChange={set("description")} />
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Benefits / Advantages (isa bawat linya)</Label>
              <Textarea className="mt-1 min-h-24" value={benefitsText} onChange={(e) => setBenefitsText(e.target.value)} placeholder={"Benefit 1\nBenefit 2\nBenefit 3"} />
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Features (isa bawat linya)</Label>
              <Textarea className="mt-1 min-h-20" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"Feature 1\nFeature 2"} data-testid="product-form-features" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Science behind infused elements</Label>
                <Button type="button" variant="outline" size="sm" className="h-7" onClick={addScience} data-testid="product-form-science-add">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Dagdag
                </Button>
              </div>
              <div className="mt-2 space-y-3">
                {(form.science || []).length === 0 && (
                  <p className="text-xs font-medium text-foreground">Wala pang naka-list na element. I-tap ang “Dagdag” para maglagay (hal. Graphene, Terahertz, Sea Cucumber).</p>
                )}
                {(form.science || []).map((s, i) => (
                  <div key={i} className="rounded-lg border border-border bg-background/60 p-2" data-testid={`product-form-science-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="h-9 flex-1" value={s.title || ""} onChange={(e) => updateScience(i, "title", e.target.value)} placeholder="Element (hal. Graphene)" data-testid={`product-form-science-title-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeScience(i)} data-testid={`product-form-science-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea className="mt-2 min-h-16" value={s.desc || ""} onChange={(e) => updateScience(i, "desc", e.target.value)} placeholder="Maikling paliwanag ng agham sa likod nito" data-testid={`product-form-science-desc-${i}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 items-end gap-3">
              <div>
                <Label className="text-xs font-medium text-foreground">Presyo (PHP)</Label>
                <Input type="number" min={0} className="mt-1 h-10" value={form.pricePHP} onChange={set("pricePHP")} data-testid="product-form-price" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={!!form.isFeatured} onCheckedChange={(v) => setForm((s) => ({ ...s, isFeatured: v }))} id="featured" data-testid="product-form-featured" />
                <Label htmlFor="featured" className="text-sm">Featured</Label>
              </div>
            </div>
            <MultiImageUploader label="Mga larawan ng produkto (gallery)" value={form.images} onChange={(v) => setForm((s) => ({ ...s, images: v }))} testid="product-form-images" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Kanselahin</Button>
              <Button onClick={save} disabled={saving} data-testid="product-form-save">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-save...</> : "I-save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
